import {
  createReassembler,
  encodeFrames,
  nextMsgId,
  resolveChunkBudgetBytes,
  type FrameReassembler,
} from '@okclawpro/shared/transport/datachannel-framing'

export interface PublicAccessLink {
  deviceCode: string
  signalingUrl: string
}

export interface PublicAccessTicketLink {
  deviceId: string
  ticket: string
  userId: string
  signalingUrl: string
}

export type PublicAccessConnectionTarget = PublicAccessLink | PublicAccessTicketLink

export interface PublicAccessIceServer {
  urls: string | string[]
  username?: string
  credential?: string
}

export interface PublicAccessPairing {
  deviceCode: string
  deviceId?: string
  connectionId: string
  stunUrls?: string[]
  iceServers?: PublicAccessIceServer[]
  iceTransportPolicy?: RTCIceTransportPolicy
  allowRelay?: boolean
  relayEnabled?: boolean
  workspaceId?: string
  locale?: string
}

export interface PublicAccessDisconnectedEvent {
  type: 'browser.disconnected' | 'desktop.offline'
  deviceCode?: string
  deviceId?: string
  connectionId?: string
  reason?: string
  pairing?: PublicAccessPairing
}

export type ConnectPublicAccessSignalingOptions = PublicAccessConnectionTarget & {
  timeoutMs?: number
  createWebSocket?: (url: string) => WebSocket
  useRelay?: boolean
  clientInfo?: PublicAccessBrowserClientInfo
  onDisconnected?: (event: PublicAccessDisconnectedEvent) => void
}

export interface PublicAccessBrowserClientInfo {
  browserName?: string
  os?: string
}

export interface PublicAccessPeerOptions {
  connectionId: string
  signalingSocket: Pick<WebSocket, 'send' | 'addEventListener' | 'removeEventListener'>
  stunUrls?: string[]
  iceServers?: PublicAccessIceServer[]
  iceTransportPolicy?: RTCIceTransportPolicy
  timeoutMs?: number
  createPeerConnection?: (configuration: RTCConfiguration) => RTCPeerConnection
}

export interface PublicAccessPeerConnection {
  socketPromise: Promise<WebSocketLike>
  close: () => void
}

export interface PublicAccessSignalingConnection {
  promise: Promise<PublicAccessPairing>
  close: () => void
  socket: WebSocket
}

export interface WebSocketLike {
  readonly OPEN: number
  readonly CONNECTING: number
  readonly CLOSING: number
  readonly CLOSED: number
  readonly readyState: number
  onopen: ((event?: Event) => void) | null
  onmessage: ((event: MessageEvent) => void) | null
  onclose: ((event?: Event) => void) | null
  onerror: ((event?: Event) => void) | null
  send(data: string): void
  close(): void
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function normalizeIceServers(value: unknown): PublicAccessIceServer[] | undefined {
  if (!Array.isArray(value)) return undefined
  const servers = value
    .filter(isRecord)
    .map((entry): PublicAccessIceServer | null => {
      const urls = entry.urls
      const normalizedUrls = typeof urls === 'string'
        ? urls.trim()
        : Array.isArray(urls)
          ? urls.filter((url): url is string => typeof url === 'string' && url.trim().length > 0).map(url => url.trim())
          : ''
      if (typeof normalizedUrls === 'string' && !normalizedUrls) return null
      if (Array.isArray(normalizedUrls) && normalizedUrls.length === 0) return null
      return {
        urls: normalizedUrls,
        username: typeof entry.username === 'string' ? entry.username : undefined,
        credential: typeof entry.credential === 'string' ? entry.credential : undefined,
      }
    })
    .filter((entry): entry is PublicAccessIceServer => entry !== null)
  return servers.length > 0 ? servers : undefined
}

function resolveIceServers(options: Pick<PublicAccessPeerOptions, 'iceServers' | 'stunUrls'>): RTCIceServer[] {
  if (options.iceServers && options.iceServers.length > 0) {
    return options.iceServers.map((server) => ({
      urls: server.urls,
      username: server.username,
      credential: server.credential,
    }))
  }
  return (options.stunUrls ?? []).map((url) => ({ urls: url }))
}

function normalizeIceTransportPolicy(value: unknown): RTCIceTransportPolicy | undefined {
  return value === 'relay' || value === 'all' ? value : undefined
}

function shouldRetryWithRelay(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error)
  return message.includes('Public peer-to-peer connection')
}

function isTicketLink(options: PublicAccessConnectionTarget): options is PublicAccessTicketLink {
  return 'deviceId' in options && 'ticket' in options && 'userId' in options
}

function normalizeMetadata(value: unknown, maxLength: number): string | undefined {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed ? trimmed.slice(0, maxLength) : undefined
}

function resolveBrowserClientInfo(): PublicAccessBrowserClientInfo | undefined {
  if (typeof navigator === 'undefined') return undefined
  const userAgent = navigator.userAgent
  const browserName = /Edg\//.test(userAgent)
    ? 'Microsoft Edge'
    : /Firefox\//.test(userAgent)
      ? 'Firefox'
      : /Chrome\//.test(userAgent)
        ? 'Chrome'
        : /Safari\//.test(userAgent)
          ? 'Safari'
          : 'Browser'
  const os = /Windows/i.test(userAgent)
    ? 'Windows'
    : /Mac OS X|Macintosh/i.test(userAgent)
      ? 'macOS'
      : /Android/i.test(userAgent)
        ? 'Android'
        : /iPhone|iPad|iPod/i.test(userAgent)
          ? 'iOS'
          : /Linux/i.test(userAgent)
            ? 'Linux'
            : undefined
  return {
    browserName,
    ...(os ? { os } : {}),
  }
}

function normalizeClientInfo(value: PublicAccessBrowserClientInfo | undefined): PublicAccessBrowserClientInfo | undefined {
  const browserName = normalizeMetadata(value?.browserName, 80)
  const os = normalizeMetadata(value?.os, 80)
  if (!browserName && !os) return undefined
  return {
    ...(browserName ? { browserName } : {}),
    ...(os ? { os } : {}),
  }
}

export function resolvePublicAccessLink(href: string): PublicAccessLink | null {
  const url = new URL(href)
  const segments = url.pathname.split('/').filter(Boolean)
  if (segments[0] !== 'd' || !segments[1]) return null

  return {
    deviceCode: decodeURIComponent(segments[1]),
    signalingUrl: `${url.protocol === 'https:' ? 'wss:' : 'ws:'}//${url.host}/signal`,
  }
}

export function connectPublicAccessSignaling(
  options: ConnectPublicAccessSignalingOptions,
): PublicAccessSignalingConnection {
  const timeoutMs = options.timeoutMs ?? 10_000
  const createWebSocket = options.createWebSocket ?? ((url: string) => new WebSocket(url))
  const socket = createWebSocket(options.signalingUrl)
  let settled = false
  let timeout: ReturnType<typeof setTimeout> | null = null
  let activePairing: PublicAccessPairing | null = null

  const clear = () => {
    if (timeout) {
      clearTimeout(timeout)
      timeout = null
    }
  }

  const promise = new Promise<PublicAccessPairing>((resolve, reject) => {
    const fail = (message: string) => {
      if (settled) return
      settled = true
      clear()
      reject(new Error(message))
      try { socket.close() } catch {}
    }

    if (timeoutMs > 0) {
      timeout = setTimeout(() => {
        fail('Public access signaling timeout')
      }, timeoutMs)
    }

    socket.onopen = () => {
      const clientInfo = normalizeClientInfo(options.clientInfo ?? resolveBrowserClientInfo())
      const connectMessage = isTicketLink(options)
        ? {
            type: 'browser.connect',
            deviceId: options.deviceId,
            ticket: options.ticket,
            userId: options.userId,
            ...(options.useRelay ? { useRelay: true } : {}),
            ...(clientInfo ? { clientInfo } : {}),
          }
        : {
            type: 'browser.connect',
            deviceCode: options.deviceCode,
            ...(options.useRelay ? { useRelay: true } : {}),
            ...(clientInfo ? { clientInfo } : {}),
          }
      socket.send(JSON.stringify(connectMessage))
    }

    socket.onmessage = (event) => {
      let message: unknown
      try {
        message = JSON.parse(String(event.data))
      } catch {
        fail('Invalid public access signaling response')
        return
      }

      if (!isRecord(message) || typeof message.type !== 'string') {
        fail('Invalid public access signaling response')
        return
      }

      if (message.type === 'browser.connected') {
        if (typeof message.deviceCode !== 'string' || typeof message.connectionId !== 'string') {
          fail('Invalid public access pairing response')
          return
        }
        settled = true
        clear()
        const pairing: PublicAccessPairing = {
          deviceCode: message.deviceCode,
          deviceId: typeof message.deviceId === 'string' && message.deviceId.trim()
            ? message.deviceId.trim()
            : undefined,
          connectionId: message.connectionId,
          stunUrls: Array.isArray(message.stunUrls)
            ? message.stunUrls.filter((url): url is string => typeof url === 'string' && url.trim().length > 0)
            : undefined,
          iceServers: normalizeIceServers(message.iceServers),
          iceTransportPolicy: normalizeIceTransportPolicy(message.iceTransportPolicy),
          allowRelay: message.allowRelay === true,
          relayEnabled: message.relayEnabled === true,
          workspaceId: typeof message.workspaceId === 'string' && message.workspaceId.trim()
            ? message.workspaceId.trim()
            : undefined,
          locale: typeof message.locale === 'string' && message.locale.trim()
            ? message.locale.trim()
            : undefined,
        }
        activePairing = pairing
        resolve(pairing)
        return
      }

      if (message.type === 'browser.disconnected' || message.type === 'desktop.offline') {
        options.onDisconnected?.({
          type: message.type,
          deviceCode: typeof message.deviceCode === 'string' ? message.deviceCode : undefined,
          deviceId: typeof message.deviceId === 'string' ? message.deviceId : undefined,
          connectionId: typeof message.connectionId === 'string' ? message.connectionId : undefined,
          reason: typeof message.reason === 'string' ? message.reason : undefined,
          pairing: activePairing ?? undefined,
        })
        try { socket.close() } catch {}
        return
      }

      if (message.type === 'error') {
        fail(typeof message.message === 'string' ? message.message : 'Public access signaling error')
      }
    }

    socket.onerror = () => {
      fail('Public access signaling connection failed')
    }

    socket.onclose = () => {
      if (!settled) {
        fail('Public access signaling connection closed')
      }
    }
  })

  return {
    promise,
    socket,
    close: () => {
      settled = true
      clear()
      socket.close()
    },
  }
}

function mapDataChannelReadyState(state: RTCDataChannelState): number {
  switch (state) {
    case 'connecting':
      return WebSocket.CONNECTING
    case 'open':
      return WebSocket.OPEN
    case 'closing':
      return WebSocket.CLOSING
    case 'closed':
      return WebSocket.CLOSED
  }
}

const DATA_CHANNEL_BACKPRESSURE_TIMEOUT_MS = 250

/** Wait once for the channel's buffered amount to drain below its low threshold. */
function waitForBufferedAmountLow(
  channel: RTCDataChannel,
  timeoutMs = DATA_CHANNEL_BACKPRESSURE_TIMEOUT_MS,
): Promise<'low' | 'timeout'> {
  return new Promise((resolve) => {
    let timeout: ReturnType<typeof setTimeout> | null = null
    let settled = false

    const cleanup = () => {
      if (timeout) {
        clearTimeout(timeout)
        timeout = null
      }
      channel.removeEventListener('bufferedamountlow', onLow)
    }

    const finish = (reason: 'low' | 'timeout') => {
      if (settled) return
      settled = true
      cleanup()
      resolve(reason)
    }

    const onLow = () => finish('low')

    channel.addEventListener('bufferedamountlow', onLow)
    if (timeoutMs > 0) {
      timeout = setTimeout(() => finish('timeout'), timeoutMs)
    }
  })
}

/**
 * Serialized, backpressure-aware sender for a DataChannel.
 *
 * CRITICAL: all sends must go through ONE in-order queue. Framed messages are split
 * into multiple frames whose reassembly on the peer requires the frames to arrive
 * contiguously and in order. If a second send (even a single-frame one) interleaves
 * its frame between another message's frames, the peer reassembler sees an
 * out-of-sequence chunk and drops the whole message. A single-flight pump over a FIFO
 * queue guarantees one message's frames are fully flushed before the next begins.
 */
function createFrameSender(channel: RTCDataChannel): (frames: string[], budgetBytes: number) => void {
  const queue: string[] = []
  let pumping = false

  const pump = async () => {
    if (pumping) return
    pumping = true
    try {
      while (queue.length > 0) {
        if (channel.readyState !== 'open') break
        const frame = queue[0]
        const budget = frame.length
        const high = Math.max(8 * budget, 256 * 1024)
        if (channel.bufferedAmount > high) {
          await waitForBufferedAmountLow(channel)
          if (channel.readyState !== 'open') break
        }
        channel.send(frame)
        queue.shift()
      }
    } finally {
      pumping = false
      if (queue.length > 0 && channel.readyState === 'open') void pump()
    }
  }

  return (frames: string[]) => {
    for (const frame of frames) queue.push(frame)
    void pump()
  }
}

export function createDataChannelSocket(
  channel: RTCDataChannel,
  getMaxMessageSize?: () => number | undefined,
): WebSocketLike {
  let msgId = 0
  let budget: number | null = null
  let reassembler: FrameReassembler
  const enqueueFrames = createFrameSender(channel)

  const socket: WebSocketLike = {
    OPEN: WebSocket.OPEN,
    CONNECTING: WebSocket.CONNECTING,
    CLOSING: WebSocket.CLOSING,
    CLOSED: WebSocket.CLOSED,
    get readyState() {
      return mapDataChannelReadyState(channel.readyState)
    },
    onopen: null,
    onmessage: null,
    onclose: null,
    onerror: null,
    send(data: string) {
      try {
        if (budget == null) budget = resolveChunkBudgetBytes(getMaxMessageSize?.())
        msgId = nextMsgId(msgId)
        // All sends (single- AND multi-frame) go through the one serialized sender
        // so a small message can never interleave between another message's frames.
        enqueueFrames(encodeFrames(data, msgId, budget), budget)
      } catch (error) {
        socket.onerror?.({ message: error instanceof Error ? error.message : String(error) } as unknown as Event)
      }
    },
    close() {
      channel.close()
    },
  }

  reassembler = createReassembler({
    onError: (error) => socket.onerror?.({ message: error.message } as unknown as Event),
  })

  channel.addEventListener('open', (event) => {
    budget = resolveChunkBudgetBytes(getMaxMessageSize?.())
    channel.bufferedAmountLowThreshold = budget
    socket.onopen?.(event)
  })
  channel.addEventListener('message', (event) => {
    const out = reassembler.push(String((event as MessageEvent).data))
    if (out != null) socket.onmessage?.({ data: out } as MessageEvent)
  })
  channel.addEventListener('close', (event) => {
    socket.onclose?.(event)
  })
  channel.addEventListener('error', (event) => {
    socket.onerror?.(event)
  })

  return socket
}

function waitForDataChannelOpen(channel: RTCDataChannel, timeoutMs: number): Promise<void> {
  if (channel.readyState === 'open') return Promise.resolve()

  return new Promise((resolve, reject) => {
    let timeout: ReturnType<typeof setTimeout> | null = null

    const cleanup = () => {
      if (timeout) {
        clearTimeout(timeout)
        timeout = null
      }
      channel.removeEventListener('open', onOpen)
      channel.removeEventListener('close', onClose)
      channel.removeEventListener('error', onError)
    }

    const onOpen = () => {
      cleanup()
      resolve()
    }
    const onClose = () => {
      cleanup()
      reject(new Error('Public peer-to-peer connection closed before it was ready'))
    }
    const onError = () => {
      cleanup()
      reject(new Error('Public peer-to-peer connection failed'))
    }

    channel.addEventListener('open', onOpen)
    channel.addEventListener('close', onClose)
    channel.addEventListener('error', onError)

    if (timeoutMs > 0) {
      timeout = setTimeout(() => {
        cleanup()
        reject(new Error(`Public peer-to-peer connection timeout after ${timeoutMs}ms`))
      }, timeoutMs)
    }
  })
}

export function connectPublicAccessPeer(options: PublicAccessPeerOptions): PublicAccessPeerConnection {
  const timeoutMs = options.timeoutMs ?? 30_000
  const createPeerConnection = options.createPeerConnection
    ?? ((configuration: RTCConfiguration) => new RTCPeerConnection(configuration))
  const peer = createPeerConnection({
    iceServers: resolveIceServers(options),
    iceTransportPolicy: options.iceTransportPolicy,
  })
  const channel = peer.createDataChannel('okclaw-rpc', { ordered: true })
  const socket = createDataChannelSocket(channel, () => peer.sctp?.maxMessageSize)

  const sendSignal = (message: unknown) => {
    options.signalingSocket.send(JSON.stringify(message))
  }

  const handleSignal = (event: MessageEvent) => {
    let message: unknown
    try {
      message = JSON.parse(String(event.data))
    } catch {
      return
    }
    if (!isRecord(message) || message.connectionId !== options.connectionId) return

    if (message.type === 'webrtc.answer' && typeof message.sdp === 'string') {
      void peer.setRemoteDescription({ type: 'answer', sdp: message.sdp })
      return
    }

    if (message.type === 'webrtc.iceCandidate' && isRecord(message.candidate)) {
      void peer.addIceCandidate(message.candidate as RTCIceCandidateInit)
    }
  }

  options.signalingSocket.addEventListener('message', handleSignal)
  peer.addEventListener('icecandidate', (event) => {
    if (!event.candidate) return
    sendSignal({
      type: 'webrtc.iceCandidate',
      connectionId: options.connectionId,
      candidate: event.candidate.toJSON(),
    })
  })

  const socketPromise = (async () => {
    const offer = await peer.createOffer()
    await peer.setLocalDescription(offer)
    if (!offer.sdp) throw new Error('Failed to create public access WebRTC offer')
    sendSignal({
      type: 'webrtc.offer',
      connectionId: options.connectionId,
      sdp: offer.sdp,
    })
    await waitForDataChannelOpen(channel, timeoutMs)
    return socket
  })()

  return {
    socketPromise,
    close: () => {
      options.signalingSocket.removeEventListener('message', handleSignal)
      try { channel.close() } catch {}
      peer.close()
    },
  }
}

export async function connectPublicAccessPeerWithFallback(
  publicLink: PublicAccessConnectionTarget,
  options: {
    signalingTimeoutMs?: number
    peerTimeoutMs?: number
    createWebSocket?: (url: string) => WebSocket
    createPeerConnection?: (configuration: RTCConfiguration) => RTCPeerConnection
    onPairing?: (pairing: PublicAccessPairing) => Promise<void> | void
    onDisconnected?: (event: PublicAccessDisconnectedEvent) => void
    refreshTicket?: () => Promise<PublicAccessTicketLink>
  } = {},
): Promise<{
  pairing: PublicAccessPairing
  signaling: PublicAccessSignalingConnection
  peer: PublicAccessPeerConnection
  socket: WebSocketLike
}> {
  const connectAttempt = async (useRelay: boolean) => {
    const signaling = connectPublicAccessSignaling({
      ...publicLink,
      timeoutMs: options.signalingTimeoutMs,
      createWebSocket: options.createWebSocket,
      useRelay,
      onDisconnected: options.onDisconnected,
    })
    let peer: PublicAccessPeerConnection | null = null
    try {
      const pairing = await signaling.promise
      await options.onPairing?.(pairing)
      peer = connectPublicAccessPeer({
        connectionId: pairing.connectionId,
        signalingSocket: signaling.socket,
        stunUrls: pairing.stunUrls,
        iceServers: pairing.iceServers,
        iceTransportPolicy: pairing.iceTransportPolicy,
        timeoutMs: options.peerTimeoutMs,
        createPeerConnection: options.createPeerConnection,
      })
      const socket = await peer.socketPromise
      return { pairing, signaling, peer, socket }
    } catch (error) {
      peer?.close()
      signaling.close()
      throw error
    }
  }

  try {
    return await connectAttempt(false)
  } catch (error) {
    if (!shouldRetryWithRelay(error)) throw error
    if (isTicketLink(publicLink) && options.refreshTicket) {
      publicLink = await options.refreshTicket()
    }
    return connectAttempt(true)
  }
}
